import { useEffect, useRef, useState } from "react";
import { photoDetail } from "../lib/api";
import { deterministicExif, megapixels, tagsFromAlt } from "../lib/exif";
import {
  Heart, Download, ExternalLink, X, Camera, Aperture, Timer, Gauge, FolderPlus,
} from "lucide-react";
import SimilarStrip from "./SimilarStrip";
import { useAuth } from "../lib/auth";
import SaveToCollectionPopover from "./SaveToCollectionPopover";
import DownloadSizeSheet from "./DownloadSizeSheet";

const TIERS = [
  { name: "Personal", price: "$9", desc: "Blogs, social, personal projects." },
  { name: "Commercial", price: "$49", desc: "Ads, marketing, product pages.", featured: true },
  { name: "Extended", price: "$199", desc: "Merch, resale, unlimited reach." },
];

export default function PhotoDrawer({ photoId, initialPhoto, open, onClose, onOpenPhoto }) {
  const [photo, setPhoto] = useState(initialPhoto || null);
  const [loading, setLoading] = useState(!initialPhoto);
  const [error, setError] = useState("");
  const [collPopoverOpen, setCollPopoverOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const collBtnRef = useRef(null);

  const { isAuthed, isFavorited, toggleFavorite, requireAuth } = useAuth();
  const faved = photo ? isFavorited(photo.id) : false;

  useEffect(() => {
    if (!open || !photoId) return;
    if (initialPhoto && String(initialPhoto.id) === String(photoId)) {
      setPhoto(initialPhoto);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    photoDetail(photoId)
      .then((p) => setPhoto(p))
      .catch((e) => setError(e?.response?.data?.detail?.error || "Failed to load photo"))
      .finally(() => setLoading(false));
  }, [photoId, open, initialPhoto]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // reset internal panels when photo changes or drawer closes
  useEffect(() => {
    setCollPopoverOpen(false);
    setDownloadOpen(false);
  }, [photoId, open]);

  const exif = photo ? deterministicExif(photo.id) : null;
  const tags = photo ? tagsFromAlt(photo.alt) : [];

  const onSaveClick = () => {
    if (!photo) return;
    toggleFavorite(photo);
  };

  const onCollectionsClick = () => {
    if (!photo) return;
    requireAuth(() => setCollPopoverOpen((v) => !v));
  };

  const onDownloadClick = () => {
    if (!photo) return;
    requireAuth(() => setDownloadOpen(true));
  };

  return (
    <>
      <div
        onClick={onClose}
        data-testid="drawer-backdrop"
        className={`fixed inset-0 z-40 bg-ink/25 backdrop-blur-[1.5px] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        data-testid="photo-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[520px] md:w-[45vw] lg:w-[42vw] xl:w-[40vw] bg-ivory shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="font-serif text-lg text-ink" data-testid="drawer-title">Photo details</div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 space-y-4" data-testid="drawer-loading">
              <div className="w-full aspect-[3/2] es-shimmer" />
              <div className="h-4 w-2/3 es-shimmer" />
              <div className="h-3 w-1/2 es-shimmer" />
            </div>
          )}

          {error && <div className="p-6 text-sm text-destructive" data-testid="drawer-error">{error}</div>}

          {photo && !loading && (
            <div className="p-6 space-y-8">
              <div
                className="relative w-full bg-secondary overflow-hidden"
                style={{
                  backgroundColor: photo.avg_color || "#e9e5dd",
                  aspectRatio: `${photo.width && photo.height ? photo.width / photo.height : 1}`,
                }}
              >
                <img
                  data-testid="drawer-image"
                  src={photo.src?.large || photo.src?.medium}
                  alt={photo.alt || ""}
                  className="absolute inset-0 w-full h-full object-contain es-fade-in"
                />
              </div>

              <div>
                <h2 className="font-serif text-2xl md:text-3xl leading-tight text-ink" data-testid="drawer-photo-title">
                  {photo.alt || "Untitled photograph"}
                </h2>
                <div className="mt-3 text-sm text-muted-foreground">
                  by{" "}
                  <a
                    href={photo.photographer?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink es-gold-underline hover:text-gold"
                    data-testid="drawer-photographer-link"
                  >
                    {photo.photographer?.name}
                  </a>{" "}
                  · via Depositphotos
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onDownloadClick}
                  data-testid="drawer-download-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-ivory hover:bg-gold/90 transition-colors text-sm tracking-wide"
                >
                  <Download className="h-4 w-4" /> Download
                </button>
                <button
                  onClick={onSaveClick}
                  data-testid="drawer-favorite-btn"
                  className={`inline-flex items-center gap-2 px-5 py-3 border transition-colors text-sm tracking-wide ${
                    faved
                      ? "bg-gold border-gold text-ivory hover:bg-gold/90"
                      : "border-ink text-ink hover:bg-gold hover:border-gold hover:text-ivory"
                  }`}
                >
                  <Heart className={`h-4 w-4 ${faved ? "fill-current" : ""}`} />
                  {faved ? "Saved" : "Save"}
                </button>

                <div className="relative">
                  <button
                    ref={collBtnRef}
                    onClick={onCollectionsClick}
                    data-testid="drawer-collections-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 border border-border text-ink hover:border-ink transition-colors text-sm tracking-wide"
                  >
                    <FolderPlus className="h-4 w-4" /> Collections
                  </button>
                  {isAuthed && (
                    <SaveToCollectionPopover
                      photo={photo}
                      open={collPopoverOpen}
                      onClose={() => setCollPopoverOpen(false)}
                      anchorRef={collBtnRef}
                    />
                  )}
                </div>

                <a
                  href={photo.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="drawer-external-link"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-border text-ink hover:border-ink transition-colors text-sm tracking-wide"
                >
                  <ExternalLink className="h-4 w-4" /> Depositphotos
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4 border-y border-border py-5">
                <MetaRow label="Dimensions" value={`${photo.width}×${photo.height}`} />
                <MetaRow label="Resolution" value={megapixels(photo.width, photo.height)} />
                <MetaRow
                  label="Average color"
                  value={
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-4 w-4 rounded-sm border border-border" style={{ backgroundColor: photo.avg_color || "#e9e5dd" }} />
                      <span className="font-mono text-xs">{photo.avg_color || "—"}</span>
                    </span>
                  }
                />
                <MetaRow
                  label="Orientation"
                  value={photo.width === photo.height ? "Square" : photo.width > photo.height ? "Landscape" : "Portrait"}
                />
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Capture data</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <ExifCard icon={<Camera className="h-4 w-4" />} label="Camera" value={exif.camera} />
                  <ExifCard icon={<Aperture className="h-4 w-4" />} label="Lens" value={exif.lens} />
                  <ExifCard icon={<Aperture className="h-4 w-4" />} label="Aperture" value={exif.aperture} />
                  <ExifCard icon={<Timer className="h-4 w-4" />} label="Shutter" value={exif.shutter} />
                  <ExifCard icon={<Gauge className="h-4 w-4" />} label="ISO" value={exif.iso} />
                  <ExifCard icon={<Camera className="h-4 w-4" />} label="Focal" value={exif.focalLength} />
                </div>
              </div>

              {tags.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Tags</div>
                  <div className="flex flex-wrap gap-2" data-testid="drawer-tags">
                    {tags.map((t) => (
                      <a
                        key={t}
                        href={`/dashboard/search?q=${encodeURIComponent(t)}`}
                        className="text-xs px-3 py-1 border border-border hover:border-ink hover:bg-white transition-colors"
                      >
                        {t}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">License</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="drawer-license-tiers">
                  {TIERS.map((t) => (
                    <div
                      key={t.name}
                      className={`p-4 border ${t.featured ? "border-ink bg-white" : "border-border bg-white/60"} relative`}
                    >
                      {t.featured && (
                        <div className="absolute -top-2 left-3 text-[9px] tracking-[0.3em] uppercase bg-ink text-ivory px-2 py-0.5">
                          Popular
                        </div>
                      )}
                      <div className="font-serif text-lg">{t.name}</div>
                      <div className="text-2xl font-serif text-ink mt-1">{t.price}</div>
                      <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{t.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <SimilarStrip photo={photo} onOpen={onOpenPhoto} />
            </div>
          )}
        </div>
      </aside>

      {photo && (
        <DownloadSizeSheet
          photo={photo}
          open={downloadOpen}
          onClose={() => setDownloadOpen(false)}
        />
      )}
    </>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="text-sm text-ink mt-1">{value}</div>
    </div>
  );
}

function ExifCard({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 border border-border bg-white px-3 py-2">
      <span className="text-gold shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="text-sm text-ink truncate">{value}</div>
      </div>
    </div>
  );
}
